import { anyOf, arrayOf, enumOf, is, objectOf } from "@altostra/type-validations";
import { nullOrUndefined, number, string } from "@altostra/type-validations/lib/primitives";

import {
  callWebhookToolType,
  getCustomAttributeToolType,
  getUserAttributeToolType,
  getWebhookResponseToolType,
  knowledgeSourceToolType,
  logCustomEventToolType,
  setCustomAttributeToolType,
  workflowAttributeGetterToolType,
  workflowAttributeSetterToolType,
} from "./types";

const nonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

export const isKnowledgeSourceToolConfig = objectOf({
  tool_type: is(knowledgeSourceToolType),
  definition_id: nonEmptyString,
});

export const isWorkflowAttributeSetterToolConfig = objectOf({
  tool_type: is(workflowAttributeSetterToolType),
  definition_id: nonEmptyString,
});

export const isWorkflowAttributeGetterToolConfig = objectOf({
  tool_type: is(workflowAttributeGetterToolType),
  definition_id: nonEmptyString,
});

export const isLogCustomEventToolConfig = objectOf({
  tool_type: is(logCustomEventToolType),
  definition_id: nonEmptyString,
});

export const isSetCustomAttributeToolConfig = objectOf({
  tool_type: is(setCustomAttributeToolType),
  definition_id: nonEmptyString,
});

export const isGetCustomAttributeToolConfig = objectOf({
  tool_type: is(getCustomAttributeToolType),
  marker_id: nonEmptyString,
  attribute_name: nonEmptyString,
});

export const isGetUserAttributeToolConfig = objectOf({
  tool_type: is(getUserAttributeToolType),
  marker_id: nonEmptyString,
  attribute_name: nonEmptyString,
});

export const isCallWebhookToolConfig = objectOf({
  tool_type: is(callWebhookToolType),
  definition_id: nonEmptyString,
});

export const isGetWebhookResponseToolConfig = objectOf({
  tool_type: is(getWebhookResponseToolType),
  marker_id: nonEmptyString,
  definition_id: nonEmptyString,
  response_field_name: nonEmptyString,
});

export const isToolConfig = anyOf(
  isKnowledgeSourceToolConfig,
  isWorkflowAttributeSetterToolConfig,
  isWorkflowAttributeGetterToolConfig,
  isLogCustomEventToolConfig,
  isSetCustomAttributeToolConfig,
  isGetCustomAttributeToolConfig,
  isGetUserAttributeToolConfig,
  isCallWebhookToolConfig,
  isGetWebhookResponseToolConfig
);

export const isConversationalWorkflowStep = objectOf({
  instruction: string,
  tool_configs: arrayOf(isToolConfig),
});

export const isConversationalWorkflowChatSetting = objectOf({
  app_ids: arrayOf(string),
});

export const isConversationalWorkflowMessagingChannelSetting = objectOf({
  subscription_group_ids: arrayOf(string),
});

export const isConversationalWorkflowResultObject = objectOf({
  id: string,
  name: string,
  purpose: string,
  status: enumOf("draft", "active"),
  segment_ids: arrayOf(string),
  steps: arrayOf(isConversationalWorkflowStep),
  chat_setting: anyOf(nullOrUndefined, isConversationalWorkflowChatSetting),
  messaging_channel_setting: anyOf(nullOrUndefined, isConversationalWorkflowMessagingChannelSetting),
  created_by_email: string,
  created_at: string,
  updated_at: string,
  territory_ids: arrayOf(string),
  tags: arrayOf(string),
});

export const isConversationalWorkflowResult = objectOf({
  result: isConversationalWorkflowResultObject,
});

export const isOperatorSafeConversationalWorkflowStep = objectOf({
  instruction: string,
});

export const isOperatorSafeConversationalWorkflowResultObject = objectOf({
  id: string,
  name: string,
  purpose: string,
  status: enumOf("draft", "active"),
  step_count: number,
  steps: arrayOf(isOperatorSafeConversationalWorkflowStep),
});

export const isOperatorSafeConversationalWorkflowResult = objectOf({
  result: isOperatorSafeConversationalWorkflowResultObject,
});

export const isGetConversationalWorkflowResult = anyOf(
  isConversationalWorkflowResult,
  isOperatorSafeConversationalWorkflowResult
);
